import { Box } from '@chakra-ui/react';
import { type MouseEvent, useEffect, useMemo, useRef } from 'react';
import type { SingleValue } from 'react-select';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import type {
  PrimaryEntity,
  PrimaryEntityOption,
} from '../../PageConfigProvider/types/chart';
import { usePageState } from '../../PageState/PageStateProvider';
import { useChart } from '../ChartProvider';
import { StyledPlotSelect } from '../StyledPlotSelect';

export const PrimaryEntitySwitcher = ({
  isSingleEntityChart,
  primaryFilterId,
}: {
  isSingleEntityChart: boolean;
  primaryFilterId: PrimaryFilterId;
}) => {
  const { item } = useChart();
  const { pageState, dispatch: pageDispatch } = usePageState();

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryFilterId);

  const { primaryEntitySwitcher } = item?.plot ?? {};

  const primaryEntityOptions: PrimaryEntityOption[] = useMemo(() => {
    return (
      primaryFilterState?.primarySelections
        ?.filter((selection) => selection?.isActive)
        ?.map((selection) => ({
          label: selection?.name,
          value: {
            id: selection?.id.split('_').pop() || selection?.id,
            name: selection?.name,
            levelId: selection.levelId,
          },
        })) || []
    );
  }, [primaryFilterState?.primarySelections]);

  const optionValues = useMemo(() => {
    return primaryEntityOptions.map((o) => o.value);
  }, [primaryEntityOptions]);

  const selectedPrimaryEntityOption = (() => {
    const selectedEntity = pageState.primaryEntities?.[item?.id];
    if (!selectedEntity) return null;

    return primaryEntityOptions.find(
      (option) =>
        option.value.levelId === selectedEntity.levelId &&
        option.value.id === selectedEntity.id
    );
  })();

  // Determine if we should display the switcher (after we have options).
  const showSwitcher =
    primaryEntitySwitcher ||
    (isSingleEntityChart && primaryEntityOptions.length > 1);

  /** When going from a single primary entity selection to multiple primary selections,
   * use the last single selection as the default switcher value */
  const lastSingleSelection = useRef<PrimaryEntity | null>(null);
  if (primaryEntityOptions.length === 1) {
    lastSingleSelection.current = primaryEntityOptions?.[0]?.value ?? null;
  }

  useEffect(() => {
    const lastIsValid = lastSingleSelection.current
      ? optionValues.some(
          (v) =>
            v.levelId === lastSingleSelection.current?.levelId &&
            v.id === lastSingleSelection.current?.id
        )
      : false;

    const defaultPrimaryEntity = lastIsValid
      ? lastSingleSelection.current
      : (optionValues?.[0] ?? null);

    if (showSwitcher && !selectedPrimaryEntityOption && defaultPrimaryEntity) {
      pageDispatch({
        type: 'SET_PRIMARY_ENTITY',
        payload: { plotId: item?.id, primaryEntity: defaultPrimaryEntity },
      });
    }

    if (!showSwitcher && pageState.primaryEntities?.[item?.id]) {
      pageDispatch({
        type: 'CLEAR_PRIMARY_ENTITIES',
        payload: { plotIds: [item?.id] },
      });
    }
  }, [
    showSwitcher,
    item?.id,
    pageDispatch,
    optionValues,
    selectedPrimaryEntityOption,
    pageState.primaryEntities,
  ]);

  const handleSelectedPrimaryFilterChange = (
    entity: SingleValue<PrimaryEntityOption>
  ) => {
    if (!entity) return;

    pageDispatch({
      type: 'SET_PRIMARY_ENTITY',
      payload: { plotId: item?.id, primaryEntity: entity.value },
    });
  };

  if (!showSwitcher || primaryEntityOptions.length === 0) {
    return null;
  }

  return (
    <Box width="100%" onClick={(e: MouseEvent) => e.stopPropagation()}>
      <StyledPlotSelect<PrimaryEntityOption>
        value={selectedPrimaryEntityOption}
        onChange={handleSelectedPrimaryFilterChange}
        options={primaryEntityOptions}
        height="24px"
        classNamePrefix="primary-switch"
        isOptionSelected={(option) => {
          const optionValue = option.value;
          const selectedOptionValue = selectedPrimaryEntityOption?.value;
          return (
            optionValue.levelId === selectedOptionValue?.levelId &&
            optionValue.id === selectedOptionValue?.id
          );
        }}
      />
    </Box>
  );
};
